import React, { useState, useEffect } from "react";
import arcBridge from 'frontend/js/arcBridge';
import shopify from "../../../../../js/shopify";
import hasher from "../../../../../js/alpine/stores/hasher";

interface Props {
  settings: any;
  firstName?: string;
  recentOrder?: {
    status: string;
    financial_status?: string;
    label: string;
  };
}

interface Store {
  id: string;
  name: string;
  address_street_1?: string;
  address_street_2?: string;
  city?: string;
  state?: string;
  postcode?: string | number;
  country?: string;
  phone_number?: string;
}

const AccountDashboard = ({ settings, firstName, recentOrder }: Props) => {
  const [rewards, setRewards] = useState({ tier: "Silver", balance: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleSavePreferredStore = async (storeToSave?: Store) => {
      const store = storeToSave || JSON.parse(localStorage.getItem("preferredStore") || "{}");
      if (!store) return;
  
      try {
        const customerId = window.customerId;
  
        if (!customerId) {
          return;
        }
  
        const query = hasher.store().getQuery('customer_metafields_update');
  
        const variables = {
          input: {
            id: `gid://shopify/Customer/${customerId}`,
            metafields: [
              {
                namespace: "custom",
                key: "store_id",
                type: "single_line_text_field",
                value: store.id || "",
              },
              {
                namespace: "custom",
                key: "store_name",
                type: "single_line_text_field",
                value: store.name || "",
              },
              {
                namespace: "custom",
                key: "address_street_1",
                type: "single_line_text_field",
                value: store.address_street_1 || "",
              },
              {
                namespace: "custom",
                key: "address_street_2",
                type: "single_line_text_field",
                value: store.address_street_2 || "",
              },
              {
                namespace: "custom",
                key: "city",
                type: "single_line_text_field",
                value: store.city || "",
              },
              {
                namespace: "custom",
                key: "state",
                type: "single_line_text_field",
                value: store.state || "",
              },
              {
                namespace: "custom",
                key: "postcode",
                type: "single_line_text_field",
                value: store.postcode?.toString() || "",
              },
              {
                namespace: "custom",
                key: "country",
                type: "single_line_text_field",
                value: store.country || "",
              },
              {
                namespace: "custom",
                key: "phone_number",
                type: "single_line_text_field",
                value: store.phone_number || "",
              }
            ]
          }
        };
  
        const body = JSON.stringify({
          query,
          variables,
        });
  
        const fetchDetails = {
          options: {
            method: 'POST',
            headers: {
              'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
              'Content-Type': 'application/json',
              'X-MindArc-Hash': hasher.store().getHash('customer_metafields_update'),
            },
            body: body,
            request_body: query,
          },
        };
  
        await shopify.authenticatedFetch(fetchDetails);
      } catch (error) {
      }
    };
    const fetchPreferredStore = async () => {
      try {
        const customerId = window.customerId;
        if (!customerId) return;

        const query = hasher.store().getQuery('get_customer_metafields');
        const fetchDetails = {
          options: {
            method: 'POST',
            headers: {
              'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
              'Content-Type': 'application/json',
              'X-MindArc-Hash': hasher.store().getHash('get_customer_metafields'),
            },
            body: JSON.stringify({
              query,
              variables: {
                customerId: `gid://shopify/Customer/${customerId}`
              }
            }),
            request_body: query,
          },
        };

        const response = await shopify.authenticatedFetch(fetchDetails);
        const metafields = response?.data?.customer?.metafields?.edges || [];

        const storeData: Record<string, string> = {};
        metafields.forEach(({ node }) => {
          storeData[node.key] = node.value;
        });

        if (Object.keys(storeData).length > 0) {
          const store = {
            id: storeData.store_id || '',
            name: storeData.store_name || '',
            address_street_1: storeData.address_street_1 || '',
            address_street_2: storeData.address_street_2 || '',
            city: storeData.city || '',
            state: storeData.state || '',
            postcode: storeData.postcode || '',
            country: storeData.country || '',
            phone_number: storeData.phone_number || '',
          };
          
          if (store.id) {
            localStorage.setItem("preferredStore", JSON.stringify(store));
            localStorage.setItem("preferredStoreName", store.name);
            window.dispatchEvent(new CustomEvent("preferredStoreChanged", { detail: store }));
          }else{
            handleSavePreferredStore();
          }
        }
      } catch (err) {
        console.error("Error fetching preferred store metafields", err);
      }
    };
    fetchPreferredStore();
  }, [])

  useEffect(() => {
    async function savedPreferredStore(e) {
      const { tier = "Silver", points = 0 } = e.detail;
      setRewards({
        tier,
        balance: points,
      });
      setLoading(false);
    };
    window.addEventListener("preferredStoreUpdatedAccountDashboard", savedPreferredStore);
    return () => {
      window.removeEventListener("preferredStoreUpdatedAccountDashboard", savedPreferredStore);
    };
  }, []);

  const renderOrderStatus = () => {
    if (!recentOrder) return <div className="text-black">No recent orders</div>;

    switch (recentOrder.status) {
      case "complete":
        return <div className="text-[#36573B]">Delivered</div>;
      case "partial":
        return <div className="text-[#FA700C]">Partially Delivered</div>;
      case "fulfilled":
        return <div className="text-[#36573B]">Fulfilled</div>;
      case "unfulfilled":
        return <div className="text-[#FA700C]">In Process</div>;
      default:
        if (
          ["refunded", "voided", "expired"].includes(
            recentOrder.financial_status || ""
          )
        ) {
          return <div className="text-[#D12727]">Cancelled</div>;
        }
        return <div className="text-black">{recentOrder.label}</div>;
    }
  };
  
  return (
    <>
      <div className="w-full">
        <div className="account-header">
          <h1 className="flex items-end h2 account-header__title">
            <span className="lg:flex-none">Welcome back, {firstName}</span>
          </h1>
          <div className="icon-arrow-down-rounded account-header__icon"></div>
        </div>

        <div className="grid grid-cols-1 gap-5 mt-12 lg:grid-cols-2">
          <a
            href="/account?view=my-rewards"
            className="grid items-start border border-[#BBBBBD] p-4 lg:p-6 hover:no-underline hover:shadow-custom hover:bg-[#f5f5f5] hover:border-none"
          >
            <span className="w-20 h-[2px] bg-[#A2A9AD] mb-6"></span>
            <p className="pb-2 uppercase bc text-[#54565A]">
              My Rewards
            </p>
            <h3 className={loading ? "bc text-[#54565A] !normal-case" : "text-[#00263E] mb-2"}>
              {loading ? "Loading..." : rewards.tier}
            </h3>
            <div className="min-h-[150px] flex flex-col">
              <label className="blabel font-medium text-[#54565A]">
                Balance
              </label>
              <span className="text-black bcp">
                ${rewards.balance}
              </span>
            </div>
            <div className="flex self-end justify-between">
              <label className="text-[#00263E] text-sm/[18px] lg:text-base/[16px]">
                View Rewards
              </label>
              <i className="text-2xl text-[#00263E] icon-chevron-right"></i>
            </div>
          </a>

          <a
            href="/account?view=orders"
            className="grid items-start border border-[#BBBBBD] p-4 lg:p-6 hover:no-underline hover:shadow-custom hover:bg-[#f5f5f5] hover:border-none"
          >
            <span className="w-20 h-[2px] bg-[#A2A9AD] mb-6"></span>
            <p className="pb-2 uppercase bc text-[#54565A]">My Order History</p>
            <h3 className="text-[#00263E] mb-2">Recent Order</h3>
            <div className="min-h-[150px] flex flex-col">
              <label className="blabel font-medium text-[#54565A]">Status</label>
              <span className="text-black bcp">{renderOrderStatus()}</span>
            </div>
            <div className="flex self-end justify-between">
                <label className="text-[#00263E] text-sm/[18px] lg:text-base/[16px]">
                  See Order Details
                </label>
                <i className="text-2xl text-[#00263E] icon-chevron-right"></i>
            </div>
          </a>

          <a href="/account?view=personal-details" className="grid items-start border border-[#BBBBBD] p-4 lg:p-6 hover:no-underline hover:shadow-custom hover:bg-[#f5f5f5] hover:border-none">
            <span className="w-20 h-[2px] bg-[#A2A9AD] mb-6"></span>
            <p className="pb-2 uppercase bc text-[#54565A]">My Details</p>
            <h3 className="text-[#00263E] mb-2">Personal Details</h3>
            <div className="min-h-[150px] flex flex-col">
              <label className="blabel font-medium text-[#54565A]"></label>
              <span className="text-black bcp"></span>
            </div>
            <div className="flex self-end justify-between">
              <label className="text-[#00263E] text-sm/[18px] lg:text-base/[16px]">
                View Details
              </label>
              <i className="text-2xl text-[#00263E] icon-chevron-right"></i>
            </div>
          </a>
        </div>
      </div>
    </>
  );
};

export default AccountDashboard;